import { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";

// 334: Doklad přetažením — na PC stačí PDF / fotku faktury přetáhnout z pošty nebo
// ze složky kamkoli do okna; otevře se stejné čtení dokladu jako po „Nahrát fakturu".
// Místa s vlastním přetahováním (fotky zakázky, úkol, uzávěrka dne) mají přednost:
// jejich onDragOver / onDrop volají preventDefault, takže sem dorazí s defaultPrevented.
const DOC_EXT = /\.(pdf|jpe?g|png|webp|heic|heif)$/i;
export const isDocFile = (f) => !!f && (f.type === "application/pdf" || (f.type || "").startsWith("image/") || DOC_EXT.test(f.name || ""));
const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
// Otevřené okno (dialog, šuplík, popover) — doklad by se otevřel přes něj; `accept`
// to dovolí, když je otevřené právě čtení dokladu (další soubor jde do fronty).
const dialogOpen = () => !!document.querySelector('[role="dialog"]');

// 386: `hint` = jiný text pruhu (na Objednávkách se faktura nenahrává, jen se ukáže, kam přílohu dát).
export function useDocDrop({ enabled = true, acceptOverDialog = false, onFiles, hint = null }) {
  const [over, setOver] = useState(false);
  const timer = useRef(null);
  const opts = useRef({});
  opts.current = { acceptOverDialog, onFiles };

  useEffect(() => {
    if (!enabled) return undefined;
    const hide = () => { clearTimeout(timer.current); setOver(false); };
    const blocked = () => dialogOpen() && !opts.current.acceptOverDialog;
    const onOver = (e) => {
      if (!hasFiles(e)) return;
      if (e.defaultPrevented) { hide(); return; }   // soubor si bere zóna stránky
      e.preventDefault();                            // jinak prohlížeč PDF otevře místo aplikace
      if (blocked()) { e.dataTransfer.dropEffect = "none"; hide(); return; }
      e.dataTransfer.dropEffect = "copy";
      setOver(true);
      // dragleave z okna spolehlivě nechodí → pruh zmizí, když přestanou chodit dragover
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setOver(false), 250);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      hide();
      if (e.defaultPrevented) return;
      e.preventDefault();
      if (blocked()) return;
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) opts.current.onFiles?.(files);
    };
    window.addEventListener("dragenter", onOver);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onOver);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
      hide();
    };
  }, [enabled]);

  const overlay = over ? (
    <div className="fixed inset-0 z-[80] pointer-events-none p-3 md:p-5" data-testid="doc-drop-overlay" aria-hidden="true">
      <div className="h-full w-full rounded-[24px] border-2 border-dashed border-brand bg-background/70 backdrop-blur-[2px] grid place-items-center motion-safe:animate-in motion-safe:fade-in duration-150">
        <div className="mx-4 max-w-xs rounded-[20px] border border-border bg-card px-6 py-5 text-center shadow-xl">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-brand/10 text-brand"><FileText size={24} /></span>
          <p className="text-[17px] font-bold">{hint ? hint.title : "Pusťte doklad sem"}</p>
          <p className="mt-1 text-sm text-muted-foreground">{hint ? hint.text : "Fakturu nebo účtenku přečteme a nabídneme k uložení. Jde i víc souborů naráz."}</p>
        </div>
      </div>
    </div>
  ) : null;

  return { overlay };
}
